import React, { useCallback, useEffect, useState } from 'react'
import { Home as IconHome } from 'react-feather'
import { FormattedMessage } from 'react-intl'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Input, CardTitle, Button, Spinner, Row } from 'reactstrap'

import Breadcrumb from '~/components/Breadcrumb'
import { Col, LoadableCard, Show } from '~/components/commons'
import { toast } from 'react-toastify'
import useAxios from 'axios-hooks'
import { Alert } from '~/components/Alert'
import MultiSelect from '~/components/MultiSelect'
import Checkbox from '~/components/Checkbox'

function CreateNewAccount() {
  const [alertVisible, setAlertVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [accountName, setAccountName] = useState('')
  const [isUpdate, setIsUpdate] = useState(false)
  const [selects, setSelects] = useState()
  const [importProject, setImportProject] = useState(false)
  const [importKeywords, setImportKeywords] = useState(false)
  const [legacyInsights, setLegacyInsights] = useState(false)
  const [unifiedProject, setUnifiedProject] = useState(false)
  const [users, setUsers] = useState([])
  const [whitelistIds, setWhitelistIds] = useState()
  const history = useHistory()
  const { accountId } = useParams()

  const [, createAccount] = useAxios(
    { url: '/admin/accounts', method: 'POST' },
    { manual: true }
  )
  const [, updateAccount] = useAxios(
    { url: `/admin/accounts/${accountId}`, method: 'PATCH' },
    { manual: true }
  )

  const [, getAccount] = useAxios(
    { url: `/admin/accounts/${accountId}`, method: 'GET' },
    { manual: true }
  )

  const [, getUsersAdmin] = useAxios(
    { url: `/admin/accounts/${accountId}/admin-users`, method: 'GET' },
    { manual: true }
  )

  const fillSelecteUsers = useCallback(() => {
    const usersSelected = users.filter(u => whitelistIds.includes(...u.value))
    setSelects(usersSelected)
  }, [users, whitelistIds])

  useEffect(() => {
    if (accountId) {
      getUsersAdmin().then(({ data }) => {
        if (data.users) {
          const userOptions = data.users?.map(u => ({
            label: u.name,
            value: [u.id],
          }))
          setUsers(userOptions || [])
        }
      })

      getAccount().then(({ data: account }) => {
        setAccountName(account.name)
        setImportProject(account.importProject)
        setImportKeywords(account.importKeywords)
        setLegacyInsights(account.legacyInsights)
        setUnifiedProject(account.unifiedProject)
        setWhitelistIds(account.whiteListAdminIds)
        setIsUpdate(true)
      })
    }
  }, [accountId, getAccount, getUsersAdmin])

  useEffect(() => {
    if (whitelistIds?.length && users.length) fillSelecteUsers()
  }, [whitelistIds, users, fillSelecteUsers])

  async function handleSubmitAccount() {
    try {
      const submitAction = isUpdate ? updateAccount : createAccount
      setLoading(true)

      const whiteListAdminIds = selects?.map(o => o.value).flat()

      await submitAction({
        data: {
          name: accountName,
          importProject,
          importKeywords,
          legacyInsights,
          whiteListAdminIds,
          unifiedProject,
        },
      })

      setAlertVisible(true)
      setAccountName('')
      setLoading(false)
    } catch (e) {
      console.error(e)
      toast.error(`Not possible to create this account.`)
      setLoading(false)
    }
  }

  return (
    <Col>
      <Breadcrumb
        className="mb-2"
        paths={[
          {
            render: () => (
              <Link className="d-flex align-items-center" to="/">
                <IconHome size={20} />
              </Link>
            ),
          },
          {
            render: () => (
              <Link className="d-flex align-items-center" to="/accounts">
                <FormattedMessage id="Accounts" />
              </Link>
            ),
          },
          { text: !isUpdate ? 'Create New Account' : 'Update Account' },
        ]}
      />
      <LoadableCard loading={false} style={{ padding: '30px 40px' }}>
        <CardTitle style={{ marginBottom: '20px' }}>
          <FormattedMessage
            id={!isUpdate ? 'Create New Account' : 'Update Account'}
          />
        </CardTitle>
        <Row>
          <Col md="6" className="px-1">
            <label>
              <FormattedMessage id="Account name" />
            </label>
            <Input
              value={accountName}
              onChange={e => setAccountName(e.target.value)}
            />
          </Col>
          <Show condition={accountId}>
            <Col md="6" className="px-1">
              <label>
                <FormattedMessage id="Admins whitelist" />
              </label>
              <MultiSelect
                placeholder={
                  <FormattedMessage id="Select admins to whitelist" />
                }
                options={users}
                value={selects}
                onChange={options => setSelects(options)}
              />
            </Col>
          </Show>
        </Row>

        <div>
          <Row className="ml-0">
            <Checkbox
              className="my-2"
              color="primary"
              label="Allow import projects"
              size="md"
              checked={importProject}
              onChange={() => setImportProject(prev => !prev)}
            />
            <Checkbox
              className="my-2"
              color="primary"
              label="Allow import keywords"
              size="md"
              checked={importKeywords}
              onChange={() => setImportKeywords(prev => !prev)}
            />
            <Checkbox
              className="my-2"
              color="primary"
              label="Allow to access legacy insights"
              size="md"
              checked={legacyInsights}
              onChange={() => setLegacyInsights(prev => !prev)}
            />
            <Checkbox
              className="my-2"
              color="primary"
              label="Use unified project"
              size="md"
              checked={unifiedProject}
              onChange={() => setUnifiedProject(prev => !prev)}
            />
          </Row>
          <Button
            type="button"
            color="primary"
            onClick={handleSubmitAccount}
            style={{ marginTop: '20px', width: 'max-width' }}
          >
            {loading ? (
              <Spinner color="white" size="sm" />
            ) : (
              <FormattedMessage
                id={!isUpdate ? 'Create New Account' : 'Update Account'}
              />
            )}
          </Button>
        </div>
      </LoadableCard>
      <Alert
        type="success"
        visible={alertVisible}
        onClose={() => history.push('/accounts')}
        confirmBtnText="Go to Accounts"
        onCancel={() => setAlertVisible(false)}
        hasCancelBtn={true}
        cancelBtnText="Close"
        body={
          isUpdate
            ? 'Account successfully updated!'
            : 'Account successfully created!'
        }
        title="Success"
      />
    </Col>
  )
}

export default CreateNewAccount
